import { useState, useEffect } from 'react';
import { toast } from "sonner";
import { FaWifi, FaExclamationTriangle, FaRedo } from 'react-icons/fa';
import '../style/transactionTable.css';

// Services
import { getTransactionByDomainId, getTransactionById, deleteTransaction } from '../../../service/api/TransactionService';
import useSelectedDomain from '../../../service/Hooks/useSelectedDomain';
import useExecutionNotices from '../../../service/Hooks/useExecutionNotices';

// Utilitaires
import { filterAndSortTransactions } from '../../../service/utils/transactionUtils';
import TransactionFilters from './components/TransactionFilters';
import TransactionTableRow from './components/TransactionTableRow';
import ConfirmationModal from '../../Validateurs/ConfirmationModal';
import OrderPagination from '../../Order/OrderTable/components/OrderPagination';
import TransactionDetailsModal from '../TransactionDetailsModal';
import { PropTypes } from 'prop-types';
import TableSkeleton from '../../TableSkeleton';
import TransactionFiltersSkeleton from './components/TransactionFiltersSkeleton';

/**
 * Bannière de statut connexion professionnelle
 */
const ConnectionBanner = ({ isOffline, onRetry }) => {
  if (!isOffline) return null;
  
  return (
    <div className="connection-banner-transaction">
      <div className="banner-content">
        <div className="banner-icon">
          <FaWifi />
        </div>
        <div className="banner-text">
          <strong>Mode hors ligne</strong>
          <span>Les transactions ne peuvent pas être synchronisées</span>
        </div>
        <button onClick={onRetry} className="banner-btn-retry">
          <FaRedo />
          Réessayer
        </button>
      </div>
    </div>
  );
};

ConnectionBanner.propTypes = {
  isOffline: PropTypes.bool,
  onRetry: PropTypes.func
};

/**
 * État vide professionnel
 */
const EmptyState = ({ isOffline, hasFilters, onRetry }) => (
  <div className="empty-state-transactions">
    <div className="empty-icon">
      {isOffline ? (
        <FaExclamationTriangle size={48} color="#F59E0B" />
      ) : (
        <div className="empty-illustration">📊</div>
      )}
    </div>
    <h5>
      {isOffline ? "Problème de connexion" : "Aucune transaction trouvée"}
    </h5>
    <p>
      {isOffline 
        ? "Impossible de charger les données. Vérifiez votre connexion internet."
        : hasFilters 
          ? "Aucune transaction ne correspond à vos critères de recherche."
          : "Aucune transaction n'est disponible pour ce domaine."
      }
    </p>
    {isOffline && (
      <button onClick={onRetry} className="btn btn-primary">
        <FaRedo className="me-2" />
        Rafraîchir
      </button>
    )}
  </div>
);

EmptyState.propTypes = {
  isOffline: PropTypes.bool,
  onRetry: PropTypes.func,
  hasFilters: PropTypes.bool
};
/**
 * COMPOSANT PRINCIPAL TABLEAU DES TRANSACTIONS
 */
export default function TransactionTables({ refresh }) {
  const selectedDomain = useSelectedDomain();
  
  // États de gestion des données
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isOffline, setIsOffline] = useState(false);
  const [error, setError] = useState(null);
  
  // États de pagination et filtres
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedStatusFilter, setSelectedStatusFilter] = useState('');
  const [selectedDateFilter, setSelectedDateFilter] = useState('');
  const [sortOrder, setSortOrder] = useState('');
  
  // États de gestion des modals
  const [showModal, setShowModal] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [transactionToDelete, setTransactionToDelete] = useState(null);
  const [isFiltering, setIsFiltering] = useState(false);
  
  // const timerRef = useRef(null);
  const transactionsPerPage = 8;

  // Hook pour les avis d'exécution
  const { 
    loading: noticeLoading, 
    generateNotice, 
    downloadNotice, 
    viewNotice, 
    sendNoticeByEmail,
    hasExecutionNotice,
    getExecutionNotice
  } = useExecutionNotices(selectedDomain?.id);

  /**
   * Détection intelligente des erreurs réseau
   */
  const checkIsConnectionError = (err) => {
    if (!err) return false;
    return !err.response || 
           err.code === 'ECONNABORTED' || 
           err.message?.includes('Network Error') ||
           err.message?.includes('Failed to fetch') ||
           err.message?.includes('connexion') ||
           err.message?.includes('network');
  };

  /**
   * Chargement des transactions - Version robuste
   */
  const fetchTransactions = async (showLoading = true) => {
    if (!selectedDomain?.id) return;

    if (showLoading) setLoading(true);
    setError(null);
    
    try {
      const data = await getTransactionByDomainId(selectedDomain.id);
      setTransactions(data);
      setIsOffline(false);
      setCurrentPage(1);
      
      // Notification discrète de reconnexion
      if (isOffline && data.length > 0) {
        toast.success("Connexion rétablie - Données synchronisées", { duration: 2000 });
      }
    } catch (err) {
      console.error("Erreur fetchTransactions:", err);
      
      if (checkIsConnectionError(err)) {
        setIsOffline(true);
        setError("CONNEXION_LOST");
        // Garder les anciennes données si elles existent (mode cache)
      } else {
        setIsOffline(false);
        setError(err.message ||  "Erreur lors du chargement");
        toast.error(err.message || "Erreur lors du chargement des données");
      }
    } finally {
      if (showLoading) setLoading(false);
    }
  };

  // EFFECT: Chargement initial et écouteurs réseau
  useEffect(() => {
    fetchTransactions();
    
    const handleOnline = () => {
      if (isOffline) {
        toast.info("Connexion rétablie");
        fetchTransactions(false);
      }
    };
    
    const handleOffline = () => {
      setIsOffline(true);
      toast.warning("Vous êtes hors ligne", { duration: 3000 });
    };
    
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [selectedDomain, refresh]);

  // Gestionnaire de retry
  const handleRetry = () => {
    fetchTransactions();
  };

  /**
   * Actions - Bloquées si offline
   */
  const handleExecutionNotice = async (transactionId) => {
    if (isOffline) {
      toast.error("Action impossible en mode hors ligne");
      return;
    }
    
    if (!selectedDomain?.id) {
      toast.error('Domain non sélectionné');
      return;
    }

    const hasNotice = hasExecutionNotice(transactionId);
    
    if (hasNotice) {
      const notice = getExecutionNotice(transactionId);
      await viewNotice(selectedDomain.id, notice.id);
    } else {
      await generateNotice(selectedDomain.id, transactionId);
    }
  };

  const handleDownloadNotice = async (transactionId) => {
    if (isOffline) {
      toast.error("Téléchargement impossible hors ligne");
      return;
    }
    
    if (!selectedDomain?.id) return;
    
    const notice = getExecutionNotice(transactionId);
    if (notice) {
      await downloadNotice(selectedDomain.id, notice.id, `avis-${notice.noticeNumber}.pdf`);
    }
  };

  const handleSendNoticeByEmail = async (transactionId) => {
    if (isOffline) {
      toast.error("Envoi impossible hors ligne");
      return;
    }
    
    if (!selectedDomain?.id) return;
    
    const notice = getExecutionNotice(transactionId);
    if (notice) {
      await sendNoticeByEmail(selectedDomain.id, notice.id);
    }
  };

  const handleViewTransaction = async (transactionId) => {
    if (isOffline) {
      // En offline, on peut quand même voir les détails si déjà chargés
      const cached = transactions.find(t => t.id === transactionId);
      if (cached) {
        setSelectedTransaction(cached);
        setShowModal(true);
        return;
      }
      toast.error("Détails indisponibles hors ligne");
      return;
    }
    
    try {
      const transactionDetails = await getTransactionById(transactionId);
      setSelectedTransaction(transactionDetails);
      setShowModal(true);
    } catch{
      toast.error("Erreur lors de la récupération des détails de la transaction.");
    }
  };

  const handleDeleteClick = transaction => {
    if (isOffline) {
      toast.error("Suppression impossible en mode hors ligne");
      return;
    }
    setTransactionToDelete(transaction);
    setShowDeleteModal(true);
  };

  const handleCancelDelete = () => {
    setTransactionToDelete(null);
    setShowDeleteModal(false);
  };

  const handleConfirmationDelete = async () => {
    try {
      await deleteTransaction(transactionToDelete.id);
      setTransactions(prev => prev.filter(t => t.id !== transactionToDelete.id));
      toast.success("Transaction supprimée avec succès.");
    } catch {
      toast.error("Erreur lors de la suppression de la transaction.");
    } finally {
      setTransactionToDelete(null);
      setShowDeleteModal(false);
    }
  };

  /**
   * Filtres et Pagination
   */
  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const handleStatusFilterChange = (value) => {
    setSelectedStatusFilter(value);
    setCurrentPage(1);
  };

  const handleDateFilterChange = (value) => {
    setSelectedDateFilter(value);
    setCurrentPage(1);
  };

  const handleSortOrderChange = (value) => {
    setSortOrder(value);
    setCurrentPage(1);
  };

  const handleResetFilters = () => {
    setSelectedStatusFilter('');
    setSelectedDateFilter('');
    setSortOrder('');
    setCurrentPage(1);
  };

  const handleCloseModal = () => setShowModal(false);

  // LOGIQUE SKELETONS (remplace tes calculs d'affichage)
  const filteredAndSortedTransactions = filterAndSortTransactions(
    transactions, 
    selectedStatusFilter, 
    selectedDateFilter, 
    sortOrder
  );

  const indexOfLastTransaction = currentPage * transactionsPerPage;
  const indexOfFirstTransaction = indexOfLastTransaction - transactionsPerPage;
  const currentTransactions = filteredAndSortedTransactions.slice(indexOfFirstTransaction, indexOfLastTransaction);
  const totalPages = Math.ceil(filteredAndSortedTransactions.length / transactionsPerPage);
  const hasFilters = Boolean(selectedStatusFilter || selectedDateFilter || sortOrder);

  // GESTION SKELETONS INTELLIGENTE
  const showSkeletons = loading || !selectedDomain;
  const showFiltersSkeleton = isFiltering && loading;
  // const hasData = !loading && !isOffline && !error && currentTransactions.length > 0;

  // FONCTION renderContent NOUVELLE
  const renderContent = () => {
    // 1) LOADING
    if (showSkeletons) {
      return (
        <div className="py-5">
          <TransactionFiltersSkeleton />
          <TableSkeleton columns={9} rows={6} showHeader={true} />
        </div>
      );
    }

    // 2) OFFLINE
    if (isOffline) {
      return (
        <EmptyState
          isOffline={true}
          hasFilters={false}
          onRetry={handleRetry}
        />
      );
    }

    // 3) CONTENU NORMAL (Filtres toujours visibles)
    return (
      <>
        <TransactionFilters
          selectedStatusFilter={selectedStatusFilter}
          selectedDateFilter={selectedDateFilter}
          sortOrder={sortOrder}
          onStatusFilterChange={handleStatusFilterChange}
          onDateFilterChange={handleDateFilterChange}
          onSortOrderChange={handleSortOrderChange}
          onResetFilters={handleResetFilters}
        />

        {/* Si aucun résultat */}
        {currentTransactions.length === 0 ? (
          <EmptyState
            isOffline={false}
            hasFilters={hasFilters}
            onRetry={handleRetry}
          />
        ) : (
          <table className="table trans-table table-striped table-hover border-no tbl-btn short-one">
            {/* thead ici */}
            <tbody>
              {currentTransactions.map((transaction) => (
                <TransactionTableRow
                  key={transaction.id}
                  transaction={transaction}
                  hasNotice={hasExecutionNotice(transaction.id)}
                  notice={getExecutionNotice(transaction.id)}
                  noticeLoading={noticeLoading}
                  onExecutionNotice={handleExecutionNotice}
                  onDownloadNotice={handleDownloadNotice}
                  onSendNotice={handleSendNoticeByEmail}
                  onViewTransaction={handleViewTransaction}
                  onDeleteTransaction={handleDeleteClick}
                  isOffline={isOffline}
                />
              ))}
            </tbody>
          </table>
        )}
      </>
    );
  };


  // Remplace ton RETURN par ÇA
  return (
    <div>
      <ConnectionBanner isOffline={isOffline} onRetry={handleRetry} />
      
      <div className="search-filter-container mt-1 mb-4">
        <h2 className={`card-title fw-bold text-primary-emphasis text-center text-uppercase fs-2 ${isOffline ? 'title-offline' : ''}`} 
            style={{ letterSpacing: "0.10em" }}>
          Liste des transactions
          {isOffline && <span className="offline-badge ms-2">Hors ligne</span>}
        </h2>
      </div>
      
      <div className="row mb-0">
        <div className="col-xl-12">
          <div className={`card lastest_trans ${isOffline ? 'card-offline-mode' : ''}`}>
            <div className="card-body px-3 py-0">
              <div className="table-responsive">
                {renderContent()}
              </div>

              {/* Pagination */}
              {!loading && !isOffline && totalPages > 1 && (
                <OrderPagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  onPageChange={handlePageChange}
                  disabled={isOffline}
                />
              )}
            </div>

            {/* Modal suppression INCHANGÉ */}
            {showDeleteModal && !isOffline && (
              <ConfirmationModal
                message={'Êtes-vous sûr de vouloir supprimer cette transaction ?'}
                onConfirm={handleConfirmationDelete}
                onCancel={handleCancelDelete}
              />
            )}
          </div>
        </div>
      </div>

      {/* Modal détails INCHANGÉ */}
      <TransactionDetailsModal
        show={showModal}
        onHide={handleCloseModal}
        transaction={selectedTransaction}
        domainId={selectedDomain?.id}
      />
    </div>
  );
}

TransactionTables.propTypes = {
  refresh: PropTypes.bool
};